import React from 'react';
import { COLOR_PALETTE, BaseColor } from 'app/constants/ColorPalette';
import { useClasses, IStyles } from 'app/utils/css';
import Crop from 'app/components/layout/Crop';
import Video from './Video';
import { ExtractProps } from 'app/utils/types';

export enum Position {
  Relative,
  AbsoluteAutoWidth,
  AbsoluteFullWidth,
}

const dimensionConstraints = {
  maxWidth: '100%',
  maxHeight: '100%',
};

type Props = Omit<ExtractProps<typeof Video>, 'children'> & {
  mirror?: boolean;
  crop?: number;
  disableBackground?: boolean;
  position?: Position;
  /** Style overrides for the video element container */
  overrideStyles?: IStyles;
};

const horizontalOffsetStyles = {
  left: '0',
  right: '0',
};

const verticalOffsetStyles = {
  top: '0',
  bottom: '0',
};

const PositionedVideo = ({
  mirror = false,
  disableBackground = false,
  position = Position.Relative,
  crop,
  style = {},
  overrideStyles = {},
  ...props
}: Props) => {
  const classes = useClasses({
    video: [
      {
        pointerEvents: 'none',
        // Work-around for Chrome not emitting resize event for H264
        // streams (like when coming from Safari)
        // https://bugs.chromium.org/p/chromium/issues/detail?id=782245
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      },
      !disableBackground && {
        backgroundColor: COLOR_PALETTE[BaseColor.Grey].black,
      },
      mirror && {
        transform: 'scale(-1, 1)',
      },

      position === Position.Relative && {
        position: 'relative',
        ...verticalOffsetStyles,
        ...horizontalOffsetStyles,
      },

      position === Position.AbsoluteFullWidth && {
        position: 'absolute',
        ...verticalOffsetStyles,
        ...horizontalOffsetStyles,
      },

      position === Position.AbsoluteAutoWidth && {
        position: 'absolute',
        ...verticalOffsetStyles,
      },

      overrideStyles,
    ],
  });

  return (
    <Video style={{ ...dimensionConstraints, ...style }} {...props}>{({ video, width, height }) => {
      const videoElement = (
        <div className={classes.video}>
          {video}
        </div>
      );

      return crop !== undefined ? (
        <Crop contentAspectRatio={width / height} outerAspectRatio={crop}>
          {videoElement}
        </Crop>
      ) : videoElement;
    }}</Video>
  );
};

export default PositionedVideo;
